<template>
  <div class="step-image-container">
    <div class="step-heading">
      <div class="heading-icon" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M4 16.5L8.6 11.9C9.37 11.13 10.63 11.13 11.4 11.9L16 16.5"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <path
            d="M14 14.5L15.6 12.9C16.37 12.13 17.63 12.13 18.4 12.9L20 14.5"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <circle
            cx="15.5"
            cy="7.5"
            r="1.5"
            fill="currentColor"
          />

          <rect
            x="3"
            y="4"
            width="18"
            height="16"
            rx="4"
            stroke="currentColor"
            stroke-width="1.8"
          />
        </svg>
      </div>

      <div class="heading-content">
        <div class="heading-line">
          <h2 class="step-title">
            Обложка вакансии
          </h2>

          <span class="required-badge">
            Обязательно
          </span>
        </div>

        <p class="step-description">
          Загрузите изображение, которое поможет вакансии выделиться
          в ленте и привлечь внимание кандидатов.
        </p>
      </div>
    </div>

    <div class="requirements">
      <span class="requirement">
        JPEG, PNG или WebP
      </span>

      <span class="requirement">
        До 10 МБ
      </span>

      <span class="requirement">
        Рекомендуем 16:9
      </span>
    </div>

    <div
      class="drop-area"
      :class="{
        dragging: isDragging,
        filled: Boolean(imageUrl),
        loading: isLoading,
        errorState: Boolean(errorMessage)
      }"
      role="button"
      tabindex="0"
      :aria-disabled="isLoading"
      @click="triggerFileInput"
      @keydown.enter.prevent="triggerFileInput"
      @keydown.space.prevent="triggerFileInput"
      @dragenter.prevent="handleDragEnter"
      @dragover.prevent
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        class="hidden-input"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        @change="handleFileChange"
      />

      <template v-if="imageUrl">
        <img
          :src="imageUrl"
          alt="Обложка вакансии"
          class="preview-image"
        />

        <div class="preview-shade"></div>

        <transition name="status">
          <div
            v-if="isImageReady && !isLoading"
            class="success-badge"
          >
            <span class="success-check">✓</span>
            Обложка загружена
          </div>
        </transition>

        <div
          v-if="!isLoading"
          class="preview-actions"
        >
          <button
            type="button"
            class="image-action replace-action"
            @click.stop="triggerFileInput"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 16V4M12 4L7.5 8.5M12 4L16.5 8.5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />

              <path
                d="M5 14V18C5 19.1 5.9 20 7 20H17C18.1 20 19 19.1 19 18V14"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>

            Заменить
          </button>

          <button
            type="button"
            class="image-action remove-action"
            @click.stop="removeImage"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 7H20M9 7V4H15V7M7 7L8 20H16L17 7"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            Удалить
          </button>
        </div>

        <transition name="loader">
          <div
            v-if="isLoading"
            class="loading-overlay"
          >
            <div class="upload-loader"></div>

            <div class="loading-content">
              <span class="loading-title">
                Загружаем обложку
              </span>

              <span class="loading-description">
                Подождите несколько секунд
              </span>
            </div>
          </div>
        </transition>
      </template>

      <div
        v-else
        class="upload-placeholder"
      >
        <div class="upload-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 15V4M12 4L7.5 8.5M12 4L16.5 8.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <path
              d="M5 14V18C5 19.1 5.9 20 7 20H17C18.1 20 19 19.1 19 18V14"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <div class="placeholder-content">
          <span class="placeholder-title">
            Перетащите изображение сюда
          </span>

          <span class="placeholder-description">
            или выберите его на компьютере
          </span>
        </div>

        <span class="select-button">
          Выбрать изображение
        </span>
      </div>
    </div>

    <transition name="message">
      <div
        v-if="errorMessage"
        class="error-message"
        role="alert"
      >
        <span class="error-icon">!</span>
        {{ errorMessage }}
      </div>
    </transition>

    <div
      v-if="isImageReady && !errorMessage"
      class="success-message"
    >
      <span class="success-dot"></span>
      Изображение сохранено. Можно переходить дальше.
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref
} from "vue";

import MyVacanciesService from "../../api/MyVacanciesService";

const emit = defineEmits([
  "ready",
  "valid",
  "notValid",
  "complete-step"
]);

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp"
];

const STORAGE_KEYS = {
  imageName: "vacancy_image_name",
  imageURL: "vacancy_image_url",
  imageBase64: "vacancy_preview_image_base64"
};

const fileInput = ref(null);
const selectedFile = ref(null);

const imageUrl = ref(
  localStorage.getItem(STORAGE_KEYS.imageURL) || ""
);

const uploadedImageUrl = ref(
  localStorage.getItem(STORAGE_KEYS.imageURL) || ""
);

const hasBase64 = ref(
  Boolean(localStorage.getItem(STORAGE_KEYS.imageBase64))
);

const isLoading = ref(false);
const isDragging = ref(false);
const errorMessage = ref("");

let dragDepth = 0;
let previewObjectURL = null;

const isImageReady = computed(() => {
  return (
    Boolean(uploadedImageUrl.value) &&
    hasBase64.value &&
    !isLoading.value
  );
});

function triggerFileInput() {
  if (isLoading.value) {
    return;
  }

  fileInput.value?.click();
}

function handleFileChange(event) {
  const file = event.target.files?.[0];

  if (file) {
    processFile(file);
  }
}

function handleDragEnter() {
  if (isLoading.value) {
    return;
  }

  dragDepth += 1;
  isDragging.value = true;
}

function handleDragLeave() {
  dragDepth = Math.max(0, dragDepth - 1);

  if (dragDepth === 0) {
    isDragging.value = false;
  }
}

function handleDrop(event) {
  dragDepth = 0;
  isDragging.value = false;

  if (isLoading.value) {
    return;
  }

  const file = event.dataTransfer?.files?.[0];

  if (file) {
    processFile(file);
  }
}

function validateFile(file) {
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return "Поддерживаются только изображения JPEG, PNG и WebP.";
  }

  if (file.size > MAX_FILE_SIZE) {
    return "Размер изображения не должен превышать 10 МБ.";
  }

  return null;
}

async function processFile(file) {
  const validationError = validateFile(file);

  if (validationError) {
    errorMessage.value = validationError;
    emitCurrentValidity();
    resetFileInput();
    return;
  }

  const vacancyId = localStorage.getItem(
    "current_vacancy_id"
  );

  if (!vacancyId) {
    errorMessage.value =
      "Не найден идентификатор вакансии. Вернитесь на предыдущий шаг и попробуйте снова.";

    emit("notValid");
    resetFileInput();
    return;
  }

  const previousState = {
    preview: imageUrl.value,
    uploadedURL: uploadedImageUrl.value,
    base64: localStorage.getItem(
      STORAGE_KEYS.imageBase64
    ),
    imageName: localStorage.getItem(
      STORAGE_KEYS.imageName
    )
  };

  errorMessage.value = "";
  selectedFile.value = file;
  isLoading.value = true;

  revokePreviewObjectURL();

  previewObjectURL = URL.createObjectURL(file);
  imageUrl.value = previewObjectURL;

  emit("notValid");

  try {
    const [base64, uploadedURL] = await Promise.all([
      fileToBase64(file),
      MyVacanciesService.uploadVacancyImage(
        vacancyId,
        file
      )
    ]);

    if (!uploadedURL) {
      throw new Error(
        "Сервер не вернул ссылку на изображение."
      );
    }

    localStorage.setItem(
      STORAGE_KEYS.imageName,
      file.name
    );

    localStorage.setItem(
      STORAGE_KEYS.imageURL,
      uploadedURL
    );

    localStorage.setItem(
      STORAGE_KEYS.imageBase64,
      base64
    );

    uploadedImageUrl.value = uploadedURL;
    hasBase64.value = true;

    revokePreviewObjectURL();
    imageUrl.value = uploadedURL;

    emit("ready");
    emit("valid");

    console.log(
      "✅ Обложка вакансии загружена:",
      uploadedURL
    );
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки обложки:",
      error
    );

    revokePreviewObjectURL();
    restorePreviousState(previousState);

    errorMessage.value =
      error?.message ||
      "Не удалось загрузить изображение. Попробуйте ещё раз.";

    emitCurrentValidity();
  } finally {
    isLoading.value = false;
    selectedFile.value = null;
    resetFileInput();
  }
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || "");
      const separatorIndex = result.indexOf(",");

      const base64 =
        separatorIndex >= 0
          ? result.slice(separatorIndex + 1)
          : result;

      if (!base64) {
        reject(
          new Error(
            "Не удалось обработать изображение."
          )
        );
        return;
      }

      resolve(base64);
    };

    reader.onerror = () => {
      reject(
        new Error(
          "Не удалось прочитать выбранный файл."
        )
      );
    };

    reader.readAsDataURL(file);
  });
}

function removeImage() {
  if (isLoading.value) {
    return;
  }

  revokePreviewObjectURL();

  selectedFile.value = null;
  imageUrl.value = "";
  uploadedImageUrl.value = "";
  hasBase64.value = false;
  errorMessage.value = "";

  localStorage.removeItem(
    STORAGE_KEYS.imageName
  );

  localStorage.removeItem(
    STORAGE_KEYS.imageURL
  );

  localStorage.removeItem(
    STORAGE_KEYS.imageBase64
  );

  resetFileInput();
  emit("notValid");
}

function saveImageAndContinue() {
  if (isLoading.value) {
    errorMessage.value =
      "Дождитесь завершения загрузки изображения.";

    emit("notValid");
    return;
  }

  if (!isImageReady.value) {
    errorMessage.value =
      "Добавьте обложку вакансии, чтобы продолжить.";

    emit("notValid");
    return;
  }

  errorMessage.value = "";
  emit("complete-step");
}

function restorePreviousState(state) {
  imageUrl.value = state.preview || "";
  uploadedImageUrl.value = state.uploadedURL || "";
  hasBase64.value = Boolean(state.base64);

  setOrRemoveStorage(
    STORAGE_KEYS.imageURL,
    state.uploadedURL
  );

  setOrRemoveStorage(
    STORAGE_KEYS.imageBase64,
    state.base64
  );

  setOrRemoveStorage(
    STORAGE_KEYS.imageName,
    state.imageName
  );
}

function setOrRemoveStorage(key, value) {
  if (value) {
    localStorage.setItem(key, value);
  } else {
    localStorage.removeItem(key);
  }
}

function emitCurrentValidity() {
  if (
    uploadedImageUrl.value &&
    hasBase64.value
  ) {
    emit("ready");
    emit("valid");
  } else {
    emit("notValid");
  }
}

function resetFileInput() {
  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

function revokePreviewObjectURL() {
  if (!previewObjectURL) {
    return;
  }

  URL.revokeObjectURL(previewObjectURL);
  previewObjectURL = null;
}

onMounted(() => {
  document.addEventListener(
    "save-image",
    saveImageAndContinue
  );

  emitCurrentValidity();
});

onUnmounted(() => {
  document.removeEventListener(
    "save-image",
    saveImageAndContinue
  );

  revokePreviewObjectURL();
});
</script>

<style scoped>
.step-image-container {
  width: 100%;
  max-width: 760px;

  display: flex;
  flex-direction: column;
  gap: 20px;

  margin-left: 40px;
  padding: 26px;

  box-sizing: border-box;

  background:
    linear-gradient(
      145deg,
      #ffffff 0%,
      #fbfcff 100%
    );

  border: 1px solid rgba(27, 51, 87, 0.08);
  border-radius: 26px;

  box-shadow:
    0 18px 48px rgba(30, 55, 90, 0.07);
}

.step-heading {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.heading-icon {
  width: 46px;
  height: 46px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #3597ff 0%,
      #526cf2 100%
    );

  border-radius: 15px;

  box-shadow:
    0 8px 20px rgba(45, 119, 237, 0.22);
}

.heading-icon svg {
  width: 24px;
  height: 24px;
}

.heading-content {
  min-width: 0;
  flex: 1;
}

.heading-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;

  margin-bottom: 6px;
}

.step-title {
  margin: 0;

  color: #1f2430;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.3;
}

.required-badge {
  padding: 5px 9px;

  color: #2974db;
  background: rgba(41, 116, 219, 0.09);

  border: 1px solid rgba(41, 116, 219, 0.12);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1;
}

.step-description {
  max-width: 600px;
  margin: 0;

  color: #737b8d;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.55;
}

.requirements {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.requirement {
  padding: 7px 11px;

  color: #727b8d;
  background: #f3f6fa;

  border: 1px solid rgba(33, 66, 110, 0.06);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.drop-area {
  position: relative;

  width: 100%;
  min-height: 320px;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;
  box-sizing: border-box;

  background:
    linear-gradient(
      145deg,
      rgba(242, 247, 255, 0.95),
      rgba(248, 250, 253, 0.95)
    );

  border: 2px dashed rgba(40, 135, 247, 0.3);
  border-radius: 22px;

  cursor: pointer;

  transition:
    border-color 0.25s ease,
    background-color 0.25s ease,
    box-shadow 0.25s ease,
    transform 0.25s ease;
}

.drop-area:hover:not(.loading) {
  border-color: rgba(40, 135, 247, 0.62);

  box-shadow:
    0 12px 28px rgba(31, 108, 210, 0.1);

  transform: translateY(-2px);
}

.drop-area:focus-visible {
  outline: none;

  border-color: #2887f7;

  box-shadow:
    0 0 0 4px rgba(40, 135, 247, 0.13);
}

.drop-area.dragging {
  background:
    linear-gradient(
      145deg,
      rgba(225, 239, 255, 0.98),
      rgba(238, 246, 255, 0.98)
    );

  border-color: #2887f7;

  box-shadow:
    0 0 0 6px rgba(40, 135, 247, 0.1),
    0 18px 35px rgba(31, 108, 210, 0.12);

  transform: scale(1.01);
}

.drop-area.filled {
  border-style: solid;
  border-width: 1px;
  border-color: rgba(27, 50, 82, 0.09);
}

.drop-area.loading {
  cursor: wait;
}

.drop-area.errorState:not(.filled) {
  border-color: rgba(218, 65, 78, 0.55);
  background: rgba(218, 65, 78, 0.025);
}

.hidden-input {
  display: none;
}

.upload-placeholder {
  max-width: 360px;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 36px 24px;
  text-align: center;
}

.upload-icon {
  width: 62px;
  height: 62px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 18px;

  color: #2887f7;
  background: rgba(40, 135, 247, 0.1);

  border: 1px solid rgba(40, 135, 247, 0.13);
  border-radius: 20px;

  box-shadow:
    0 9px 24px rgba(34, 110, 209, 0.11);

  transition:
    transform 0.25s ease;
}

.drop-area:hover:not(.loading) .upload-icon {
  transform: translateY(-3px) scale(1.04);
}

.upload-icon svg {
  width: 29px;
  height: 29px;
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  gap: 6px;

  margin-bottom: 20px;
}

.placeholder-title {
  color: #252b36;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 15px;
}

.placeholder-description {
  color: #858d9d;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
}

.select-button {
  min-width: 172px;

  padding: 11px 18px;

  color: #ffffff;
  background:
    linear-gradient(
      135deg,
      #2887f7 0%,
      #416ee9 100%
    );

  border-radius: 12px;

  box-shadow:
    0 8px 20px rgba(40, 112, 224, 0.2);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.drop-area:hover:not(.loading) .select-button {
  box-shadow:
    0 11px 25px rgba(40, 112, 224, 0.27);

  transform: translateY(-1px);
}

.preview-image {
  position: absolute;
  inset: 0;

  width: 100%;
  height: 100%;

  object-fit: cover;

  transition:
    transform 0.45s ease,
    filter 0.25s ease;
}

.drop-area.filled:hover:not(.loading) .preview-image {
  transform: scale(1.025);
}

.preview-shade {
  position: absolute;
  inset: 0;

  pointer-events: none;

  background:
    linear-gradient(
      to bottom,
      rgba(7, 18, 34, 0.08) 20%,
      rgba(7, 18, 34, 0.04) 50%,
      rgba(7, 18, 34, 0.52) 100%
    );
}

.success-badge {
  position: absolute;
  top: 16px;
  left: 16px;

  display: flex;
  align-items: center;
  gap: 7px;

  padding: 8px 11px;

  color: #ffffff;
  background: rgba(24, 31, 43, 0.7);

  border: 1px solid rgba(255, 255, 255, 0.19);
  border-radius: 999px;

  backdrop-filter: blur(12px);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
}

.success-check {
  width: 18px;
  height: 18px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background: #24bb73;

  border-radius: 50%;

  font-family: sans-serif;
  font-size: 11px;
  font-weight: 700;
}

.preview-actions {
  position: absolute;
  right: 16px;
  bottom: 16px;

  display: flex;
  gap: 9px;
}

.image-action {
  min-height: 38px;

  display: flex;
  align-items: center;
  gap: 7px;

  padding: 9px 13px;

  color: #ffffff;
  background: rgba(20, 27, 39, 0.72);

  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 11px;

  backdrop-filter: blur(12px);

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;

  cursor: pointer;

  transition:
    background-color 0.2s ease,
    transform 0.2s ease;
}

.image-action:hover {
  background: rgba(20, 27, 39, 0.9);
  transform: translateY(-2px);
}

.image-action svg {
  width: 16px;
  height: 16px;
}

.remove-action:hover {
  background: rgba(197, 51, 64, 0.92);
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;

  color: #ffffff;
  background: rgba(16, 25, 39, 0.72);

  backdrop-filter: blur(8px);
}

.upload-loader {
  width: 42px;
  height: 42px;

  border: 3px solid rgba(255, 255, 255, 0.22);
  border-top-color: #ffffff;
  border-radius: 50%;

  animation:
    upload-spin 0.75s linear infinite;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.loading-title {
  color: #ffffff;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;
}

.loading-description {
  color: rgba(255, 255, 255, 0.7);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 9px;

  padding: 11px 13px;

  color: #c73d4a;
  background: rgba(215, 57, 70, 0.07);

  border: 1px solid rgba(215, 57, 70, 0.12);
  border-radius: 12px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.4;
}

.error-icon {
  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  color: #ffffff;
  background: #d74755;

  border-radius: 50%;

  font-family: sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 8px;

  color: #607066;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.success-dot {
  width: 7px;
  height: 7px;

  flex-shrink: 0;

  background: #28b873;
  border-radius: 50%;

  box-shadow:
    0 0 0 4px rgba(40, 184, 115, 0.1);
}

.loader-enter-active,
.loader-leave-active,
.status-enter-active,
.status-leave-active,
.message-enter-active,
.message-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.loader-enter-from,
.loader-leave-to {
  opacity: 0;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: translateY(-7px);
}

.message-enter-from,
.message-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

@keyframes upload-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Тёмная тема */

html[data-theme="dark"] .step-image-container {
  background:
    linear-gradient(
      145deg,
      #202125 0%,
      #1d1e22 100%
    );

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 20px 50px rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .step-title {
  color: #f3f4f7;
}

html[data-theme="dark"] .step-description {
  color: #9da3b0;
}

html[data-theme="dark"] .required-badge {
  color: #71afff;
  background: rgba(65, 139, 238, 0.11);
  border-color: rgba(83, 151, 243, 0.14);
}

html[data-theme="dark"] .requirement {
  color: #a3a9b5;
  background: #292b30;
  border-color: rgba(255, 255, 255, 0.06);
}

html[data-theme="dark"] .drop-area {
  background:
    linear-gradient(
      145deg,
      #25272d,
      #22242a
    );

  border-color: rgba(92, 159, 250, 0.3);
}

html[data-theme="dark"] .drop-area:hover:not(.loading) {
  border-color: rgba(99, 165, 255, 0.65);

  box-shadow:
    0 13px 30px rgba(0, 0, 0, 0.22);
}

html[data-theme="dark"] .drop-area.dragging {
  background:
    linear-gradient(
      145deg,
      #263446,
      #242d3a
    );

  border-color: #63a6ff;
}

html[data-theme="dark"] .drop-area.filled {
  border-color: rgba(255, 255, 255, 0.08);
}

html[data-theme="dark"] .upload-icon {
  color: #68a9ff;
  background: rgba(79, 150, 246, 0.12);
  border-color: rgba(91, 158, 249, 0.14);
}

html[data-theme="dark"] .placeholder-title {
  color: #eef0f4;
}

html[data-theme="dark"] .placeholder-description {
  color: #8d94a1;
}

html[data-theme="dark"] .error-message {
  color: #ff8993;
  background: rgba(222, 67, 80, 0.09);
  border-color: rgba(230, 84, 96, 0.14);
}

html[data-theme="dark"] .success-message {
  color: #98aaa0;
}

/* Адаптация */

@media (max-width: 700px) {
  .step-image-container {
    max-width: 100%;

    margin-left: 0;
    padding: 20px;

    border-radius: 22px;
  }

  .drop-area {
    min-height: 250px;
    border-radius: 18px;
  }

  .requirements {
    gap: 6px;
  }

  .preview-actions {
    right: 12px;
    bottom: 12px;
    left: 12px;
  }

  .image-action {
    flex: 1;
    justify-content: center;
  }
}

@media (max-width: 430px) {
  .step-image-container {
    gap: 16px;
    padding: 17px;
  }

  .step-heading {
    gap: 12px;
  }

  .heading-icon {
    width: 42px;
    height: 42px;
    border-radius: 13px;
  }

  .step-title {
    font-size: 16px;
  }

  .step-description {
    font-size: 12px;
  }

  .required-badge {
    font-size: 9px;
  }

  .requirement {
    padding: 6px 9px;
    font-size: 9px;
  }

  .drop-area {
    min-height: 225px;
  }

  .upload-placeholder {
    padding: 24px 14px;
  }

  .upload-icon {
    width: 54px;
    height: 54px;

    margin-bottom: 14px;
    border-radius: 17px;
  }

  .placeholder-title {
    font-size: 13px;
  }

  .placeholder-description {
    font-size: 10px;
  }

  .select-button {
    min-width: 158px;
    padding: 10px 15px;
  }

  .success-badge {
    top: 11px;
    left: 11px;
  }

  .image-action {
    padding: 9px 10px;
    font-size: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .drop-area,
  .upload-icon,
  .select-button,
  .preview-image,
  .image-action {
    transition: none;
  }

  .upload-loader {
    animation-duration: 1.5s;
  }
}
</style>